import { isWeb } from "@helpers/platform";
import useMixedItemDist from "@hooks/useMixedItemDist";
import type { MixedItemTag } from "@memorang/types";
import { Box, DialogWrapper, Loader } from "@memorang/ui";
import { useRouter } from "expo-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView } from "react-native";
import { Dialog as PaperDialog, Text } from "react-native-paper";
import { useMasteryKeyTopicsSectionData } from "../hooks/useMasteryKeyTopicsSectionData";
import { useProethicaModuleList } from "../hooks/useProethicaModuleList";
import { ModuleListItem, type ModuleListItemProps } from "./ModuleListItem";
import { PurchaseDialog } from "./PurchaseDialog";

type Module = ModuleListItemProps & {
	id: string;
	moduleTitle?: string;
	moduleNumber?: number;
	durationLabel?: string;
};

type Props = {
	visible: boolean;
	handleClose: () => void;
};

export const QuickStudyDialog = ({ visible, handleClose }: Props) => {
	const { t } = useTranslation();
	const router = useRouter();
	const { data: mixedItemDist, isLoading } = useMixedItemDist();
	const {
		getModulesGroupedByStatus,
		startModule,
		startingModuleId,
		resolveModuleItems,
	} = useProethicaModuleList();
	const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);

	const masteryTopicsSection = useMasteryKeyTopicsSectionData(
		mixedItemDist,
		true,
	);

	const items = (masteryTopicsSection?.data ?? []) as MixedItemTag[];

	const sections = useMemo(
		() => getModulesGroupedByStatus(resolveModuleItems(items), t),
		[items, t, getModulesGroupedByStatus, resolveModuleItems],
	);

	const handleModulePress = (module: Module) => {
		if (module.locked) {
			if (isWeb) {
				setShowPurchaseDialog(true);
				return;
			}
			handleClose();
			router.push({
				pathname: "/purchase",
				params: { productType: "STUDY_PACK" },
			});
			return;
		}

		// Keep the dialog mounted so its button shows the loader; a successful
		// start/resume/report navigates away and unmounts it, while a failure
		// clears the loader and leaves the dialog open to retry.
		startModule(module.id);
	};

	return (
		<>
			<DialogWrapper
				visible={visible}
				handleClose={handleClose}
				width={520}
				showCloseButton
			>
				<PaperDialog.Title>{t("dashboard.quickStudy.title")}</PaperDialog.Title>
				<PaperDialog.ScrollArea style={{ paddingHorizontal: 0 }}>
					<ScrollView contentContainerStyle={{ paddingBottom: 16 }}>
						{isLoading || !mixedItemDist ? (
							<Loader />
						) : (
							<Box style={{ gap: 8 }}>
								<Text
									variant="bodyMedium"
									style={{ paddingHorizontal: 24, paddingBottom: 8 }}
								>
									{t("dashboard.quickStudy.subtitle")}
								</Text>
								{sections.map((section) => (
									<Box key={section.status}>
										<Text
											variant="titleSmall"
											style={{
												paddingHorizontal: 24,
												paddingVertical: 8,
												opacity: 0.8,
											}}
										>
											{section.title}
										</Text>
										{section.modules.map((module) => (
											<ModuleListItem
												key={module.id}
												{...module}
												loading={startingModuleId === module.id}
												onPress={() => handleModulePress(module)}
											/>
										))}
									</Box>
								))}
							</Box>
						)}
					</ScrollView>
				</PaperDialog.ScrollArea>
			</DialogWrapper>
			{showPurchaseDialog && (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={() => setShowPurchaseDialog(false)}
					productType="STUDY_PACK"
				/>
			)}
		</>
	);
};
